import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Sparkles, TrendingUp, TrendingDown } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Live "Expected Performance" panel: queries /api/strategies/expected_pnl whenever
 * config changes (debounced) and shows users the EV math BEFORE they start a bot.
 */
export default function BotOptimizerPanel({ config, onApplyRecommended }) {
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(false);

  // Debounce the API hit so dragging a slider doesn't spam the backend
  useEffect(() => {
    let active = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.post("/strategies/expected_pnl", config);
        if (active) setPnl(r.data);
      } catch (e) {
        if (active) setPnl(null);
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [
    config.strategy,
    config.trade_size_usd,
    config.spread_bps,
    config.stop_loss_pct,
    config.take_profit_pct,
    config.frequency_per_min,
    config.max_trades,
  ]);

  const positive = useMemo(() => pnl && pnl.per_session > 0, [pnl]);
  const Icon = positive ? TrendingUp : TrendingDown;
  const colorCls = positive ? "text-[#00E676]" : "text-[#FF3B30]";
  const bgCls = positive ? "border-[#00E676]/30 bg-[#00E676]/5" : "border-[#FF3B30]/30 bg-[#FF3B30]/5";

  return (
    <div className={`border ${bgCls} p-4 space-y-3`} data-testid="optimizer-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[#FFB300]" />
          <span className="label-xs">Expected Performance</span>
          {loading && <span className="text-[10px] text-neutral-500">computing…</span>}
        </div>
        {onApplyRecommended && (
          <Button
            data-testid="apply-recommended-btn"
            type="button"
            onClick={onApplyRecommended}
            variant="outline"
            className="h-7 px-3 text-xs border-[#007AFF] text-[#007AFF] hover:bg-[#007AFF]/10 bg-transparent"
          >
            Apply Recommended
          </Button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Cell label="Per trade" testid="ev-per-trade">
          <Icon size={12} className={`inline mr-1 ${colorCls}`} />
          <span className={`num text-lg font-[Chivo] ${colorCls}`}>
            {pnl ? `${pnl.per_trade.net >= 0 ? "+" : ""}$${pnl.per_trade.net.toFixed(3)}` : "—"}
          </span>
        </Cell>
        <Cell label="Per hour" testid="ev-per-hour">
          <span className={`num text-lg font-[Chivo] ${colorCls}`}>
            {pnl ? `${pnl.per_hour >= 0 ? "+" : ""}$${pnl.per_hour.toFixed(2)}` : "—"}
          </span>
        </Cell>
        <Cell label="Full session" testid="ev-per-session">
          <span className={`num text-lg font-[Chivo] ${colorCls}`}>
            {pnl ? `${pnl.per_session >= 0 ? "+" : ""}$${pnl.per_session.toFixed(2)}` : "—"}
          </span>
          <span className="text-[10px] text-neutral-500 num ml-1">({pnl?.session_trades ?? 0} trades)</span>
        </Cell>
      </div>

      <div className="text-[11px] text-neutral-400 leading-relaxed border-t border-border pt-2">
        Expected values are <strong>statistical averages</strong>, not guarantees. Each individual trade is randomized;
        actual session results will vary. Includes a {(pnl ? (pnl.per_trade.fee / Math.max(config.trade_size_usd, 1) * 10000).toFixed(1) : "—")}-bps round-trip fee.
      </div>
    </div>
  );
}

function Cell({ label, testid, children }) {
  return (
    <div className="bg-[#0F0F0F] border border-border p-3" data-testid={testid}>
      <div className="label-xs">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
